/**
 * TerminalConsole - Retro SCADA-style terminal container
 *
 * Reusable console with header bar, auto-scroll output area,
 * and configurable accent color. Used by EMS (gold) and PhD (purple).
 */

import React, { useRef, useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { vortxTypography } from '../core/theme';

export default function TerminalConsole({
    title = 'CONSOLE',
    accentColor = '#b28c50',
    statusLabel,
    statusRight,
    children,
    minHeight = 120,
    maxHeight = 280,
}) {
    const scrollRef = useRef(null);

    // Auto-scroll to bottom when children change
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    });

    return (
        <Paper sx={{
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #0a0e1a 0%, #0d1117 100%)',
            border: `1px solid ${accentColor}30`,
            borderRadius: 1,
        }}>
            {/* Header bar */}
            <Box sx={{
                px: 2,
                py: 1.5,
                background: `linear-gradient(90deg, ${accentColor}18 0%, transparent 100%)`,
                borderBottom: `1px solid ${accentColor}30`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: accentColor,
                        boxShadow: `0 0 6px ${accentColor}`,
                        '@keyframes consolePulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.4 },
                        },
                        animation: 'consolePulse 2s ease-in-out infinite',
                    }} />
                    <Typography sx={{
                        color: accentColor,
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                    }}>
                        {title}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {statusRight}
                    {statusLabel && (
                        <Box sx={{
                            px: 1.5,
                            py: 0.3,
                            borderRadius: 0.5,
                            border: `1px solid ${accentColor}`,
                            background: `${accentColor}18`,
                        }}>
                            <Typography sx={{
                                color: accentColor,
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                            }}>
                                {statusLabel}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Scrolling output area */}
            <Box
                ref={scrollRef}
                sx={{
                    px: 2,
                    py: 1.5,
                    minHeight,
                    maxHeight,
                    overflow: 'auto',
                    '&::-webkit-scrollbar': { width: 4 },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        background: `${accentColor}50`,
                        borderRadius: 2,
                    },
                }}
            >
                {children}
            </Box>
        </Paper>
    );
}
